import { useEffect, useState } from 'react';
import Button from '@components/atoms/ui-kit/button.tsx';
import { useAudio } from '@utils/providers/audio-context.tsx';
import Modal from '@components/atoms/modals/modal.tsx';
import Input from '@components/atoms/ui-kit/input.tsx';
import { Duration } from 'luxon';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useUpdateDocument } from '@utils/api/documents/use-update-document.tsx';
import { useCaption } from '@utils/providers/caption-context.tsx';
import { AxiosError } from 'axios';
import { BaseError } from '@utils/types/shared/base.ts';

type Props = {
  setTimeCodeOpen: (open: boolean) => void;
};

const CaptionTimecode = ({ setTimeCodeOpen }: Props) => {
  const { setTime, setTimecodeOffset, timecodeOffset } = useAudio();
  const { documentResult, session } = useCaption();
  const { mutate: updateDocument, isLoading: saveDocumentLoading } =
    useUpdateDocument();

  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(true);
  const [hours, setHours] = useState('0');
  const [minutes, setMinutes] = useState('0');
  const [seconds, setSeconds] = useState('0');
  const [milliseconds, setMilliseconds] = useState('0');

  useEffect(() => {
    if (timecodeOffset === undefined) return;

    const duration = Duration.fromObject({ seconds: parseInt(timecodeOffset) });
    const { hours, minutes, seconds, milliseconds } = duration
      .shiftTo('hours', 'minutes', 'seconds', 'milliseconds')
      .toObject();

    if (hours) setHours(hours.toString().padStart(1, '0'));
    if (minutes) setMinutes(minutes.toString().padStart(1, '0'));
    if (seconds) setSeconds(seconds.toString().padStart(1, '0'));
    if (milliseconds) setMilliseconds(milliseconds.toString().padStart(1, '0'));
  }, [timecodeOffset]);

  const handleSaveClick = () => {
    const isValidNumber = (value: string): boolean => !isNaN(parseInt(value));

    if (
      !isValidNumber(hours) ||
      !isValidNumber(minutes) ||
      !isValidNumber(seconds) ||
      !isValidNumber(milliseconds)
    ) {
      toast.error(t('invalid-time-values'));
      return;
    }

    if (!documentResult?.result || !session) {
      toast.error(t('missing-session-or-document'));
      return;
    }

    toast(t('setting-timecode-offset'));

    const totalTimeInSeconds =
      parseInt(hours) * 3600 +
      parseInt(minutes) * 60 +
      parseInt(seconds) +
      parseInt(milliseconds) / 1000;

    const offsetStr = totalTimeInSeconds.toString();
    setTimecodeOffset(offsetStr);
    setTime(totalTimeInSeconds);

    updateDocument(
      {
        documentId: documentResult.result.id,
        sessionId: session.session_id,
        timecode_offset: offsetStr
      },
      {
        onSuccess: () => {
          toast.success(t('timecode-offset-updated'));
          handleCloseModal();
        },
        onError: error => {
          const err = error as AxiosError<BaseError>;
          const message =
            err?.response?.data?.error_description ||
            err?.response?.data?.detail ||
            err?.response?.data?.error ||
            err?.response?.data?.message ||
            t('error-updating-timecode-offset');
          toast.error(message);
        }
      }
    );
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setTimeCodeOpen(false);
  };

  return (
    <Modal
      isOpen={isModalOpen}
      onClose={handleCloseModal}
      size="small"
      title={t('set-timecode')}
      caption={t('set-timecode-caption')}
      footerActions={[
        <Button
          onClick={handleCloseModal}
          label={t('cancel')}
          size={'small'}
          variant={'smoke'}
          disabled={saveDocumentLoading}
        />,
        <Button
          onClick={handleSaveClick}
          label={t('save')}
          size={'small'}
          variant={'primary'}
          loading={saveDocumentLoading}
          disabled={saveDocumentLoading}
        />
      ]}
    >
      <div className={'grid grid-cols-4 gap-2'}>
        <Input
          id="hours"
          size="small"
          value={hours}
          onChange={event => setHours(event.target.value)}
          placeholder="H"
          numeric
          className={'text-center'}
          label={t('hours')}
        />
        <Input
          id="minutes"
          size="small"
          value={minutes}
          onChange={event => setMinutes(event.target.value)}
          placeholder="M"
          numeric
          className={'text-center'}
          label={t('minutes')}
        />
        <Input
          id="seconds"
          value={seconds}
          size="small"
          onChange={event => setSeconds(event.target.value)}
          placeholder="S"
          numeric
          className={'text-center'}
          label={t('seconds')}
        />
        <Input
          id="milliseconds"
          size="small"
          value={milliseconds}
          onChange={event => setMilliseconds(event.target.value)}
          placeholder="Ms"
          numeric
          className={'text-center'}
          label={t('ms')}
        />
      </div>
    </Modal>
  );
};

export default CaptionTimecode;
